import React, { useEffect, useState } from "react";

import { useShowPane } from "src/hooks/useShowPane";
import { useHistory } from "react-router";
import { useQuery } from "src/app/methods/useQuery";
import { ReactComponent as CloseIcon } from "src/images/cross-alt.svg";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import { API_URLS } from "src/utils/API_URLS";
import axios from "axios";
import { showErrorToast } from "src/utils/methods";
import creatorPlaceholder from "src/images/creator-placeholder.svg";
import { SingleProvider } from "src/app/pages/CreatorDiscoveryTool/legacy/app/components/MultipleSocialProviders/SingleProvider/SingleProvider";
import { Pane } from "../Pane/Pane";
import { closeCreatorContentMonitorPreview } from "./methods";
import { Breadcrumbs, BreadcrumbsItem } from "../Breadcrumbs/Breadcrumbs";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import { Gallery } from "../Gallery/Gallery";
import { GalleryListElement } from "../Gallery/components/GalleryItem/GalleryItem";
import { Img } from "../Img/Img";
import { getProject } from "src/redux";

import "./CreatorContentMonitorPane.scss";

interface Props {
  onDataHub?: boolean;
}

export const CreatorContentMonitorPane = (props: Props) => {
  const { onDataHub } = props;

  const [data, setData] = useState<GalleryListElement[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const { projectBasicData } = useSelector(
    (state: RootState) => state.projectReducer,
  );
  const { tasks, workspaceTasks } = useSelector(
    (state: RootState) => state.taskReducer,
  );

  const query = useQuery();
  const creatorContentMonitorUuid =
    query.get("creatorContentMonitorUuid") || "";
  const creatorUuidForMonitor = query.get("creatorUuidForMonitor") || "";
  const projectUuidForMonitor = query.get("projectUuidForMonitor") || "";

  const tasksArray = onDataHub ? workspaceTasks : tasks;
  const taskData = tasksArray.find(
    (task) => task.taskId === creatorUuidForMonitor,
  );

  const history = useHistory();
  const dispatch = useDispatch();

  const [showPane, hidePane] = useShowPane(() => {
    closeCreatorContentMonitorPreview(history);
  });

  useEffect(() => {
    if (projectUuidForMonitor !== projectBasicData?.projectId) {
      dispatch(getProject(projectUuidForMonitor));
    }
  }, [dispatch, projectUuidForMonitor, projectBasicData?.projectId]);

  const getData = async () => {
    try {
      setLoading(true);

      const url = API_URLS.getPublicationsDetectedByCCM;
      const {
        data: { content },
      } = await axios.post(url, {
        wsTaskUuid: creatorUuidForMonitor,
        wsCreatorContentMonitorUuid: creatorContentMonitorUuid,
        wsProjectUuid: projectUuidForMonitor,
      });

      const newData = content.list[creatorUuidForMonitor];
      setData(newData);
      setKeywords(content.keywords);
    } catch (err) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Pane
      className="creator-content-monitor-preview"
      backdropClassName="creator-content-monitor-preview__backdrop"
      showPane={showPane}
      hidePane={hidePane}
    >
      <div className="creator-content-monitor-preview__topbar">
        <Breadcrumbs>
          <BreadcrumbsItem
            text={
              <IDHFormattedMessage
                id="ws_campaigns"
                defaultMessage="Campaigns"
              />
            }
            to={`/workspace/${activeWorkspaceUuid}/projects`}
          />
          <BreadcrumbsItem
            text={projectBasicData?.projectName}
            to={`/workspace/${activeWorkspaceUuid}/projects/${projectBasicData?.projectId}`}
          />
          <BreadcrumbsItem
            text={taskData?.title}
            to={`/workspace/${activeWorkspaceUuid}/projects/${projectBasicData?.projectId}?taskType=creator&creatorContentMonitorUuid=${creatorContentMonitorUuid}&creatorUuidForMonitor=${creatorUuidForMonitor}`}
          />
        </Breadcrumbs>

        <div className="creator-content-monitor-preview__topbar-buttons">
          <CloseIcon
            onClick={hidePane}
            className="creator-content-monitor-preview__topbar-close-icon"
          />
        </div>
      </div>

      <div className="creator-content-monitor-preview__content">
        <div className="creator-content-monitor-preview__content-creator">
          <Img
            src={taskData?.cover?.thumbnails.small}
            fallbackImageSrc={creatorPlaceholder}
          />

          <div className="creator-content-monitor-preview__content-creator-data">
            {taskData?.title}

            <div className="creator-content-monitor-preview__content-creator-data-providers">
              {taskData?.socialProfiles.map((profile) => (
                <SingleProvider
                  key={profile.socialProvider}
                  providerInfo={profile}
                  name={profile.socialProvider}
                  highlight
                />
              ))}
            </div>
          </div>
        </div>

        <Gallery
          config={{
            fullFetch: {
              data,
              getData,
              loading,
            },
          }}
          header={{
            title: (
              <>
                <IDHFormattedMessage
                  id="ws_publications_detection"
                  defaultMessage="Publications detection"
                />
                : {projectBasicData?.projectName}
              </>
            ),
            keywords,
          }}
          showMore
          creatorData={{
            username: taskData?.title || "",
            profilePictureUrl: taskData?.cover?.thumbnails?.small,
          }}
          enableFilters
          enableSortBy
        />
      </div>
    </Pane>
  );
};
